/**
 * Стол на стороне ленты: тумблер, назначение «контора → профиль», статусы,
 * разговор с расширением.
 *
 * Ключ AdsPower сюда не кладём — только в chrome.storage.local расширения.
 * Здесь живёт то, что можно показать на экране: включено ли, какой профиль у
 * какой конторы, жив ли API. Ошибка — красная строка, не toast на восемь секунд.
 *
 * Стол включён — клик ленты открывает кассы только в профилях AdsPower (решение
 * 0012): левая нога — в первый профиль стола, правая — во второй; контора с
 * назначенным профилем — в свой. Не вышло — красная строка, в Edge не открываем.
 */

import { create } from 'zustand';

import type { Promise_ } from '../confirm/expect';
import type { AdsProfile } from '../../extension/src/ads/client';
import type { BookFail, BookLeg, DeskStatus, ProfileDot, ScreenBox, WarmBook } from '../../extension/src/ads/desk';
import { ADS_COPY } from '../../extension/src/ads/errors';
import { DESK_CH, DESK_OK } from './deskChan';
import { loadLayout } from './deskLayout';
import { bookDeskOf, deskOf, forkColumn, splitPane } from './deskPanes';
import { rememberDeskArea } from './deskPanel';
import { venueCanon } from './venues';

export const DESK_KEY = 'forq.next.desk';
export { DESK_CH, DESK_OK };

/** С этой версии расширение открывает кассы в профиле своей конторы. */
export const DESK_BOOKS_MIN = '0.4.113';

export type DeskDot = 'off' | 'ok' | 'bad' | 'active' | 'closed';

/** Ключ назначения — канон площадки: `1xbit` и `onexbet` — один профиль. */
export function bookKey(source: string): string {
  return venueCanon(source) || source;
}

function squash(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9а-яё]+/gi, '');
}

/**
 * Профиль назван по конторе («POLYMARKET», «GGBET») — назначаем сами.
 * Совпало с двумя конторами или контора уже назначена — не трогаем.
 */
export function guessBooks(
  profiles: readonly AdsProfile[],
  venues: Readonly<Record<string, { name?: string }>>,
  have: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = { ...have };
  const taken = new Set(Object.values(have));
  for (const profile of profiles) {
    if (taken.has(profile.id)) continue;
    const want = squash(profile.name);
    if (want.length < 3) continue;
    const hits = Object.entries(venues).filter(([code, one]) => {
      const names = [code, bookKey(code), one.name ?? ''].map(squash).filter((n) => n.length >= 3);
      return names.includes(want);
    });
    const keys = [...new Set(hits.map(([code]) => bookKey(code)))];
    if (keys.length !== 1) continue;
    const key = keys[0]!;
    if (out[key]) continue;
    out[key] = profile.id;
    taken.add(profile.id);
  }
  return out;
}

export type DeskPair = readonly [string, string];

type Seats = { books: Readonly<Record<string, string>>; pair: DeskPair };

/** Своя контора — свой профиль; иначе половина стола: слот 0 — левый, 1 — правый. */
export function profileOfBook(state: Seats, source: string, slot = 0): string {
  const own = state.books[bookKey(source)] || state.books[source];
  if (own) return own;
  return state.pair[slot % 2 === 0 ? 0 : 1] || state.pair[0] || state.pair[1] || '';
}

/** Профили, которые стол должен держать открытыми. */
export function deskProfiles(state: Seats): string[] {
  const ids = [...state.pair, ...Object.values(state.books)].filter((one) => one !== '');
  return [...new Set(ids)];
}

export function deskConfigured(state: Seats): boolean {
  return deskProfiles(state).length > 0;
}

const SLOTS_KEY = 'forq.next.desk.slots';

function loadSlots(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(SLOTS_KEY) ?? '{}') as Record<string, unknown>;
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(raw)) if (value === 0 || value === 1) out[key] = value;
    return out;
  } catch {
    return {};
  }
}

export function resetDeskSlots(): void {
  try {
    localStorage.removeItem(SLOTS_KEY);
  } catch {
    //
  }
}

/**
 * Сторона стола для каждой ноги. Контора держится своего профиля: первая сторона
 * запоминается навсегда, чтобы вкладка конторы жила в одном браузере, а не
 * рождалась во втором. Своя контора в профиле стола — его сторона.
 */
export function deskSlots(state: Seats, sources: readonly string[]): number[] {
  const kept = loadSlots();
  const out = sources.map(() => -1);
  const taken = new Set<number>();
  const take = (index: number, slot: number): void => {
    out[index] = slot;
    taken.add(slot);
  };
  sources.forEach((source, index) => {
    const own = state.books[bookKey(source)] || state.books[source] || '';
    const side = own === '' ? -1 : state.pair.indexOf(own);
    if (side >= 0 && !taken.has(side)) take(index, side);
  });
  sources.forEach((source, index) => {
    if (out[index] !== -1) return;
    const was = kept[bookKey(source)];
    if (was !== undefined && !taken.has(was)) take(index, was);
  });
  sources.forEach((_source, index) => {
    if (out[index] !== -1) return;
    let free = 0;
    while (taken.has(free)) free += 1;
    take(index, free);
  });
  let changed = false;
  sources.forEach((source, index) => {
    const slot = out[index]!;
    if (slot <= 1 && kept[bookKey(source)] === undefined) {
      kept[bookKey(source)] = slot;
      changed = true;
    }
  });
  if (changed) {
    try {
      localStorage.setItem(SLOTS_KEY, JSON.stringify(kept));
    } catch {
      //
    }
  }
  return out;
}

export type DeskBox = { left: number; top: number; width: number; height: number };

/** Места окон профилей на мониторе сканера: как у автооткрытия, справа — карточка вилки. */
export function deskBoxes(screen: ScreenBox, count = 2, withFork = loadLayout().withFork): DeskBox[] {
  const desk = deskOf(screen);
  const column = withFork ? forkColumn(desk) : null;
  const area = column !== null ? bookDeskOf(desk, column) : desk;
  const out: DeskBox[] = [];
  for (let slot = 0; slot < count; slot += 1) {
    const box = splitPane(area, slot, count, 'side');
    if (box !== null) out.push({ left: box.availLeft, top: box.availTop, width: box.availWidth, height: box.availHeight });
  }
  return out;
}

/** Два первых профиля из AdsPower — стол без предварительного выбора. */
export function defaultPair(profiles: readonly AdsProfile[], have: DeskPair): DeskPair {
  const ids = profiles.map((one) => one.id);
  const left = have[0] && ids.includes(have[0]) ? have[0] : ids[0] ?? '';
  const right = have[1] && ids.includes(have[1]) && have[1] !== left
    ? have[1]
    : ids.find((one) => one !== left) ?? '';
  return [left, right];
}

/**
 * Живой стол: включён, API ответил, профили назначены. По нему PAC снимается
 * с касс — выход держит профиль AdsPower.
 */
export function deskTakesOpen(state: Seats & {
  on: boolean;
  api: DeskDot;
} = useDesk.getState()): boolean {
  return state.on === true && state.api === 'ok' && deskConfigured(state);
}

/** Клик принадлежит столу, как только тумблер включён: молчащий AdsPower — красная строка, не Edge. */
export function deskOwnsClick(state: { on: boolean } = useDesk.getState()): boolean {
  return state.on === true;
}

type Saved = {
  on?: boolean;
  port?: number;
  books?: Record<string, string>;
  pair?: [string, string];
  names?: Record<string, string>;
};

export type DeskAsk =
  | { op: 'status' }
  | { op: 'save'; token?: string; port: number; on: boolean }
  | { op: 'check' }
  | { op: 'list' }
  | { op: 'openBooks'; books: BookLeg[] }
  | { op: 'openProfiles'; ids: string[]; screen: ScreenBox; boxes?: DeskBox[]; warm?: WarmBook[] }
  | { op: 'profileDots'; ids: string[] };

export type DeskReply = {
  ok: boolean;
  error?: string;
  status?: DeskStatus;
  profiles?: AdsProfile[];
  hasKey?: boolean;
  api?: 'ok' | 'bad';
  dots?: Record<string, ProfileDot>;
  failed?: BookFail[];
  panel?: DeskBox;
};

export type DeskBridge = {
  ask: (cmd: DeskAsk) => Promise<DeskReply>;
};

function load(): Saved {
  try {
    const raw = localStorage.getItem(DESK_KEY);
    return raw === null ? {} : (JSON.parse(raw) as Saved);
  } catch {
    return {};
  }
}

function strings(raw: unknown): Record<string, string> {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string' && value !== '') out[key] = value;
  }
  return out;
}

export type DeskState = {
  on: boolean;
  port: number;
  /** канон конторы → id профиля */
  books: Record<string, string>;
  /** два профиля стола: левая и правая касса */
  pair: DeskPair;
  /** id профиля → имя, чтобы красная строка называла профиль, а не id */
  names: Record<string, string>;
  hasKey: boolean;
  api: DeskDot;
  dots: Record<string, DeskDot>;
  profiles: AdsProfile[];
  opening: boolean;
  error: string | null;
  /** зелёная строка: что стол сделал */
  note: string | null;
  setOn: (on: boolean) => void;
  setPort: (port: number) => void;
  setBook: (key: string, profileId: string) => void;
  setPair: (slot: 0 | 1, profileId: string) => void;
  setBooks: (books: Record<string, string>) => void;
  assignBooks: (keys: readonly string[], profileId: string) => void;
  setHasKey: (has: boolean) => void;
  setProfiles: (profiles: AdsProfile[]) => void;
  setDots: (api: DeskDot, dots: Record<string, DeskDot>) => void;
  setOpening: (opening: boolean) => void;
  fail: (error: string) => void;
  say: (note: string) => void;
  clearError: () => void;
};

function persist(state: DeskState): void {
  try {
    const body: Saved = {
      on: state.on,
      port: state.port,
      books: state.books,
      pair: [state.pair[0], state.pair[1]],
      names: state.names,
    };
    localStorage.setItem(DESK_KEY, JSON.stringify(body));
  } catch {
    // Приватный режим — на вкладку хватит памяти стора.
  }
}

const saved = load();

export const useDesk = create<DeskState>((set, get) => ({
  on: saved.on === true,
  port: typeof saved.port === 'number' && saved.port > 0 ? saved.port : 50325,
  books: strings(saved.books),
  pair: Array.isArray(saved.pair)
    ? [String(saved.pair[0] ?? ''), String(saved.pair[1] ?? '')]
    : ['', ''],
  names: strings(saved.names),
  hasKey: false,
  api: 'off',
  dots: {},
  profiles: [],
  opening: false,
  error: null,
  note: null,
  setOn: (on) => {
    set({ on, error: on ? get().error : null, note: null });
    persist(get());
  },
  setPort: (port) => {
    set({ port });
    persist(get());
  },
  setBook: (key, profileId) => {
    const books = { ...get().books };
    if (profileId === '') delete books[key];
    else books[key] = profileId;
    set({ books });
    persist(get());
  },
  setPair: (slot, profileId) => {
    const pair: [string, string] = [get().pair[0], get().pair[1]];
    pair[slot] = profileId;
    if (profileId !== '' && pair[slot === 0 ? 1 : 0] === profileId) pair[slot === 0 ? 1 : 0] = get().pair[slot];
    set({ pair });
    persist(get());
  },
  setBooks: (books) => {
    set({ books: strings(books) });
    persist(get());
  },
  assignBooks: (keys, profileId) => {
    const books = { ...get().books };
    for (const key of keys) {
      if (profileId === '') delete books[key];
      else books[key] = profileId;
    }
    set({ books });
    persist(get());
  },
  setHasKey: (hasKey) => set({ hasKey }),
  setProfiles: (profiles) => {
    if (profiles.length === 0) return;
    const names = { ...get().names };
    for (const one of profiles) names[one.id] = one.name;
    set({ profiles, names });
    persist(get());
  },
  setDots: (api, dots) => set({ api, dots }),
  setOpening: (opening) => set({ opening }),
  fail: (error) => set({ error, note: null }),
  say: (note) => set({ note, error: null }),
  clearError: () => set({ error: null }),
}));

export function profileName(id: string, state: { names: Readonly<Record<string, string>> } = useDesk.getState()): string {
  return state.names[id] || id;
}

let bridge: DeskBridge | null = null;

export function setDeskBridge(next: DeskBridge | null): void {
  bridge = next;
}

function screenOf(): ScreenBox {
  try {
    // DOM `Screen` не знает availLeft/availTop — это поле живого Chrome.
    const s = window.screen as Screen & { availLeft?: number; availTop?: number };
    return {
      availLeft: s.availLeft || 0,
      availTop: s.availTop || 0,
      availWidth: s.availWidth || window.screen.width || 0,
      availHeight: s.availHeight || window.screen.height || 0,
      width: window.screen.width,
      height: window.screen.height,
    };
  } catch {
    return { availWidth: 1920, availHeight: 1080, availLeft: 0, availTop: 0 };
  }
}

function askWaitMs(cmd: DeskAsk): number {
  if (import.meta.env.MODE === 'test') return 20;
  if (cmd.op === 'openProfiles') return 90_000;
  if (cmd.op === 'openBooks') return 20_000;
  if (cmd.op === 'profileDots') return 15_000;
  return 4_000;
}

function postAsk(cmd: DeskAsk): Promise<DeskReply> {
  if (typeof window === 'undefined') {
    return Promise.resolve({ ok: false, error: ADS_COPY.noExt });
  }
  const id = `desk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMsg);
      resolve({ ok: false, error: ADS_COPY.noExt });
    }, askWaitMs(cmd));
    function onMsg(event: MessageEvent): void {
      if (event.source !== window) return;
      const data = event.data as { channel?: string; id?: string } & DeskReply;
      if (data.channel !== DESK_OK || data.id !== id) return;
      window.removeEventListener('message', onMsg);
      window.clearTimeout(timer);
      resolve({
        ok: data.ok === true,
        error: data.error,
        status: data.status,
        profiles: data.profiles,
        hasKey: data.hasKey,
        api: data.api,
        dots: data.dots,
        failed: data.failed,
        panel: data.panel,
      });
    }
    window.addEventListener('message', onMsg);
    try {
      window.postMessage({ channel: DESK_CH, id, ...cmd }, window.location.origin);
    } catch {
      window.removeEventListener('message', onMsg);
      window.clearTimeout(timer);
      resolve({ ok: false, error: ADS_COPY.noExt });
    }
  });
}

export async function askDesk(cmd: DeskAsk): Promise<DeskReply> {
  const run = bridge ?? { ask: postAsk };
  return run.ask(cmd);
}

/** Список профилей приехал — запомнить имена; пустой стол занять двумя первыми. */
export function adoptProfiles(profiles: readonly AdsProfile[]): void {
  if (profiles.length === 0) return;
  const desk = useDesk.getState();
  desk.setProfiles([...profiles]);
  const pair = defaultPair(profiles, desk.pair);
  if (pair[0] !== desk.pair[0] || pair[1] !== desk.pair[1]) {
    useDesk.setState({ pair });
    persist(useDesk.getState());
  }
}

export function adoptDeskReply(reply: DeskReply): void {
  const desk = useDesk.getState();
  const profiles = reply.profiles ?? reply.status?.profiles ?? [];
  if (reply.hasKey !== undefined) desk.setHasKey(reply.hasKey === true);
  if (reply.status !== undefined) desk.setDots(reply.status.api, useDesk.getState().dots);
  adoptProfiles(profiles);
  if (reply.ok === false && reply.error !== undefined) desk.fail(reply.error);
}

function dotsOf(raw: Record<string, ProfileDot> | undefined): Record<string, DeskDot> {
  return { ...(raw ?? {}) };
}

/** Точки студии по назначенным профилям. */
export async function refreshDots(): Promise<DeskReply> {
  const ids = deskProfiles(useDesk.getState());
  const reply = await askDesk({ op: 'profileDots', ids });
  useDesk.getState().setDots(reply.api ?? (reply.ok ? 'ok' : 'bad'), dotsOf(reply.dots));
  if (!reply.ok && reply.error !== undefined) useDesk.getState().fail(reply.error);
  return reply;
}

export async function syncDesk(): Promise<void> {
  const desk = useDesk.getState();
  const saved = await askDesk({ op: 'save', port: desk.port, on: desk.on });
  desk.setHasKey(saved.hasKey === true);
  // Ключ не обязателен: при выключенной «Проверке API» AdsPower отвечает и без него.
  if (saved.ok !== true) return;
  const list = await askDesk({ op: 'check' });
  if (list.hasKey !== undefined) desk.setHasKey(list.hasKey === true);
  adoptProfiles(list.profiles ?? list.status?.profiles ?? []);
  if (!list.ok) {
    useDesk.getState().setDots('bad', {});
    if (useDesk.getState().on && list.error !== undefined) useDesk.getState().fail(list.error);
    return;
  }
  await refreshDots();
}

/** Клик ленты: ноги по профилям. Ответ — какие кассы не открылись и почему. */
export async function openDeskBooks(legs: readonly BookLeg[]): Promise<DeskReply> {
  const reply = await askDesk({ op: 'openBooks', books: [...legs] });
  const desk = useDesk.getState();
  if (reply.ok) {
    desk.clearError();
    const dots = { ...desk.dots };
    for (const leg of legs) dots[leg.profileId] = 'active';
    desk.setDots('ok', dots);
  }
  return reply;
}

export async function openDesksNow(warm: readonly WarmBook[] = []): Promise<DeskReply> {
  const desk = useDesk.getState();
  const ids = deskProfiles(desk);
  if (ids.length === 0) {
    desk.fail(ADS_COPY.noBooks);
    return { ok: false, error: ADS_COPY.noBooks };
  }
  desk.setOpening(true);
  try {
    const screen = screenOf();
    rememberDeskArea(deskOf(screen));
    const halves = deskBoxes(screen, 2);
    // Профили стола — левое и правое место; свои профили контор — туда же, по очереди.
    const boxes = ids.map((_id, index) => halves[index % 2]).filter((one): one is DeskBox => one !== undefined);
    const reply = await askDesk({ op: 'openProfiles', ids, screen, boxes, warm: [...warm] });
    const now = useDesk.getState();
    now.setDots(reply.api ?? (reply.ok ? 'ok' : 'bad'), dotsOf(reply.dots));
    const up = ids.filter((id) => reply.dots?.[id] === 'active').map((id) => profileName(id));
    const down = ids.filter((id) => reply.dots?.[id] !== 'active').map((id) => profileName(id));
    if (reply.ok && down.length === 0) {
      now.say(`Столы открыты и на связи: ${up.join(', ')}. Клик «открыть обе» откроет кассы в этих профилях.`);
    } else {
      const head = down.length > 0 ? `Не открылись: ${down.join(', ')}. ` : '';
      now.fail(`${head}${reply.error ?? ADS_COPY.down}`);
    }
    return reply;
  } finally {
    useDesk.getState().setOpening(false);
  }
}

export { ADS_COPY, screenOf };
export type { BookLeg, Promise_, WarmBook };
